/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../../store/helpers/helperReducers";
import { isError } from "../../../../utils/validations";
import { setSelectedSyslogReceiver } from "../../../../store/api/admin/reducers";
import { initialState } from "../../../../store/api/admin/initialState";
import { syslogReceiverDefault } from "../../helpers";
import { ISyslogReceiver } from "../../../../store/api/admin/types";
import { IKeyValue } from "../../types";
import { useSelector } from "react-redux";
import { isLoadingStatusSelector } from "../../../../store/helpers/helperSelectors";
import { Trash2 } from "lucide-react";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../../globals/constants";
import { sanitizeHtmlInput } from "../../../../utils/helpers";

interface IAddEditSyslogForwardProps {
  modelOpenMode?: string;
  formData: ISyslogReceiver;
  setFormData: (value: React.SetStateAction<ISyslogReceiver>) => void;
  errMsg: IKeyValue;
  setErrMsg: (value: React.SetStateAction<IKeyValue>) => void;
  submitHandler: () => Promise<void>;
  onBlurHandler: (event: any) => void;
  handleDeleteSyslogReceiver?: (param: any) => Promise<void>;
}

const AddEditSyslogReceiver: React.FC<IAddEditSyslogForwardProps> = ({
  formData,
  setFormData,
  errMsg,
  setErrMsg,
  submitHandler,
  onBlurHandler,
  modelOpenMode,
  handleDeleteSyslogReceiver,
}) => {
  const dispatch = useStoreDispatch();
  const isLoadingStatus = useSelector(isLoadingStatusSelector);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (event: any) => {
    event.preventDefault();
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  useEffect(() => {
    setErrMsg({});
  }, []);

  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (name === "enabled") {
      setFormData({ ...formData, [name]: checked, enableAuditTrail: checked, enableSystemEvent: checked });
    } else if (name === "enableAuditTrail") {
      if (!checked && !formData.enableSystemEvent) {
        setFormData({ ...formData, [name]: checked, enabled: false });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else if (name === "enableSystemEvent") {
      if (!checked && !formData?.enableAuditTrail) {
        setFormData({ ...formData, [name]: checked, enabled: false });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: checked });
    }
  };

  const handleClose = () => {
    setFormData(syslogReceiverDefault);
    dispatch(setSelectedSyslogReceiver(initialState.selectedSyslogReceiver));
    dispatch(setBasicDialogOpenFor(undefined));
    setErrMsg({});
  };

  const enableSave = !!formData.hostname && !!formData.port && !isError(errMsg);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
    dispatch(setConfirmDialog("SHEET_SYSLOG_DELETE"));
  };

  const handleConfirmDelete = async (actionName: string) => {
    setShowDeleteConfirm(false);
    if (!actionName) return;
    if (actionName.match("delete") && handleDeleteSyslogReceiver) {
      await handleDeleteSyslogReceiver(actionName);
      handleClose();
    }
  };

  const footer = (
    <div className="flex items-end justify-end w-full">
      <div className="flex items-end gap-2">
        {modelOpenMode === "Edit" && handleDeleteSyslogReceiver ? (
          <Button variant="outline" className="!text-destructive hover:!text-destructive !border-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" fill="currentColor" />
            Delete
          </Button>
        ) : null}
        <Button variant="outline" onClick={handleClose} disabled={isLoadingStatus["syslog"]}>
          Cancel
        </Button>
        <Button
          onClick={submitHandler}
          disabled={!enableSave || isLoadingStatus["syslog"]}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {showDeleteConfirm && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleConfirmDelete}>
          Are you sure you want to delete <strong>{sanitizeHtmlInput(formData?.hostname) || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <Sheet open={true} onClose={handleClose} title={`${modelOpenMode} Syslog Receiver`} footer={footer} width="520px">
      <form autoComplete="off" className="-mx-2">
        <div className="space-y-7 pt-2 rounded-md border border-border p-4 bg-[#fdfdfe] dark:bg-[#121c2e]">
          {/* Host Name */}
          <div className="space-y-2 pt-2">
            <Label htmlFor="syslog-hostname">
              Host Name <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <Input
              id="syslog-hostname"
              name="hostname"
              autoFocus
              disabled={modelOpenMode === "Edit"}
              value={formData?.hostname}
              onChange={handleChange}
              onBlur={onBlurHandler}
              maxLength={100}
              placeholder="Enter host name"
              tooltipText="Maximum length: 100 characters"
            />
            {errMsg?.hostname && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.hostname}</p>}
          </div>

          {/* Port */}
          <div className="space-y-2 w-1/5">
            <Label htmlFor="syslog-port">
              Port <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <Input
              id="syslog-port"
              name="port"
              type="number"
              value={formData?.port}
              onChange={handleChange}
              onBlur={onBlurHandler}
              min={1}
              max={65535}
              placeholder="Enter port number"
            />
            {errMsg?.port && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.port}</p>}
          </div>

          {/* Enable */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="syslog-enabled"
              checked={formData?.enabled}
              onCheckedChange={(checked: boolean) => handleCheckboxChange("enabled", !!checked)}
            />
            <Label htmlFor="syslog-enabled" className="cursor-pointer">Enable</Label>
          </div>

          {/* Sub-checkboxes for Audit Trail and System Event */}
          <div className="flex flex-col gap-3" style={{ paddingLeft: "24px" }}>
            <div className="flex items-center gap-2">
              <Checkbox
                id="syslog-audit-trail"
                disabled={!formData?.enabled}
                checked={formData?.enableAuditTrail}
                onCheckedChange={(checked: boolean) => handleCheckboxChange("enableAuditTrail", !!checked)}
              />
              <Label htmlFor="syslog-audit-trail" className="cursor-pointer">Enable Audit Trail</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="syslog-system-event"
                disabled={!formData?.enabled}
                checked={formData?.enableSystemEvent}
                onCheckedChange={(checked: boolean) => handleCheckboxChange("enableSystemEvent", !!checked)}
              />
              <Label htmlFor="syslog-system-event" className="cursor-pointer">Enable System Event</Label>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="syslog-description">Description</Label>
            <Input
              id="syslog-description"
              name="description"
              value={formData?.description}
              onChange={handleChange}
              maxLength={255}
              placeholder="Enter description"
              tooltipText="Maximum length: 255 characters"
            />
          </div>
        </div>
      </form>
    </Sheet>
    </>
  );
};
export default AddEditSyslogReceiver;
